// @flow
import styled from 'styled-components';
import { withState } from 'recompose';
import { pick } from 'ramda';
import * as React from 'react';
import { nextConfig } from '../../constants/nextConfig';

// styleRotate is turn into function to have test coverage
// https://github.com/styled-components/styled-components/issues/118#issuecomment-274050908
const styleRotate = ({ rotate }: { rotate: number } = {}) => {
  if (rotate)
    return `
      display: inline-block;
      transform: rotate(${rotate}deg);
    `;
  return `
  `;
};

const StyledPicture = styled.picture`
  ${({ order }) => (order ? `order: ${order}` : '')};
`;

const ImageWithFallback = withState('hasError', 'setError', false)(
  ({ alt, hasError, fallback, src, setError, ...rest }) => (
    <img
      onError={() => setError(true)}
      src={hasError ? fallback : src}
      alt={alt}
      {...rest}
    />
  ),
);

const filterImgProps = pick([
  'className',
  'onMouseEnter',
  'style',
  'width',
  'height',
  'onClick',
  'title',
  'id',
  'aria-hidden',
  'rotate',
  'data-testid',
]);

export const WebPImageComponent = ({
  src = '',
  alt,
  wrapperProps = {},
  imgStyle,
  fallback,
  ...props
}: any) => {
  const newSrc = src.startsWith('/cdn/')
    ? src.replace('/cdn', nextConfig.WEB_STATIC_CDN_URL)
    : src;
  // check newSrc url is WEB_STATIC_CDN_URL and ends in jpg and png
  const imgProps = filterImgProps(props);
  if (
    newSrc.startsWith(nextConfig.WEB_STATIC_CDN_URL) &&
    (newSrc.endsWith('jpg') || newSrc.endsWith('png'))
  ) {
    const webpSrc = newSrc.replace(/jpg|png/gi, 'webp');
    return (
      <StyledPicture {...wrapperProps}>
        <source srcSet={webpSrc} type="image/webp" />
        <img src={newSrc} alt={alt} {...imgProps} />
      </StyledPicture>
    );
  }
  if (fallback) {
    return (
      <ImageWithFallback
        {...imgProps}
        alt={alt}
        src={newSrc}
        fallback={fallback}
      />
    );
  }
  return <img alt={alt} {...imgProps} src={newSrc} />;
};

export const Image = styled(WebPImageComponent)`
  display: block;
  ${styleRotate};
`;

export const ThumbnailImage = styled(WebPImageComponent)`
  ${({ objectFit = 'cover' }) => `object-fit: ${objectFit}`};
  height: 100%;
  width: 100%;
`;

const ThumbnailWrapper = styled.div`
  overflow: hidden;
  position: relative;
  height: ${({ size = '50px' }) => size};
  width: ${({ size = '50px' }) => size};
  ${({ rounded }) => (rounded ? `border-radius: 50%` : '')};
`;

export const Thumbnail = ({
  src,
  size = '50px',
  rounded = true,
  name = '',
  style = {},
  dataTestId,
}: {
  src: string,
  size?: string,
  name?: string,
  rounded?: boolean,
  style?: any,
  dataTestId?: string,
}) => (
  <ThumbnailWrapper
    style={style}
    rounded={rounded}
    size={size}
    title={name}
    data-testid={dataTestId}
  >
    <ThumbnailImage src={src} alt={name} />
  </ThumbnailWrapper>
);

export const ImageWithWidth = styled(Image)`
  width: 100%;
`;

export const RountedImageWithWidth = styled(ImageWithWidth)`
  border-radius: 5px;
`;

export const ImageWithPosition = styled(ImageWithWidth)`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
`;
